import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/summaryTabStyle';
import { translate } from 'components/Translation';
import { BodyS, Caption, HeadingLBold, HeadingSBold } from 'components/Typography/Typography';
import CreditCard from '@material-ui/icons/CreditCard';
import Phone from '@material-ui/icons/PhoneOutlined';
import { PAYMENT_METHODS } from 'views/PrepaidLoyalty/commons/constants';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { PeriodUnit, Period } from 'components/CustomRepeatEvery/CustomRepeatEvery';
import ChevronRight from '@material-ui/icons/ChevronRight';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import classNames from 'classnames';
import { getRecurrenceSummaryTab } from 'views/TopUpAddOns/helpers/helperFunctions';

// @ts-ignore
const useStyles = makeStyles(styles);

const dateFormat = 'DD.MM.YYYY';

type ISummaryTabProps = {
	selectedNumbersAmount: number;
	paymentMethod: PAYMENT_METHODS;
	masterNumber: string | null;
	creditCardNumber: string | null;
	totalAmount: number;
	isRepeatEnabled: boolean;
	selectedPeriod: PeriodUnit;
	periodicity: string;
	period: Period;
	confirmingPayment?: boolean;
	selectedDonation?: types.payment.IDonation;
};

const SummaryTab = ({
	paymentMethod,
	selectedNumbersAmount,
	masterNumber,
	creditCardNumber,
	totalAmount,
	isRepeatEnabled,
	selectedPeriod,
	periodicity,
	period,
	confirmingPayment,
	selectedDonation,
}: ISummaryTabProps) => {
	const classes = useStyles();

	return (
		<div className={classes.smallTab}>
			<div
				className={classNames(classes.summaryTab, { [classes.summaryTabWithProgressBar]: !!confirmingPayment })}
			>
				<ConditionalRender
					show={!!confirmingPayment}
					onTrue={() => (
						<CustomCircularProgress
							loading
							text={translate('number.services.confirmation.loading')}
							transparent
						/>
					)}
					onFalse={() => (
						<>
							<div className={classes.headingWrapper}>
								<HeadingSBold color="primary-light">{translate('summary')}</HeadingSBold>
							</div>
							<div className={classes.summaryTabContainer}>
								<div>
									<Caption color="disabled-light" disableMargin>
										{translate('prepaid.loyalty.add.new.services.selectedNumbers')}
									</Caption>
									<HeadingLBold color="disabled-light" disableMargin>
										{selectedNumbersAmount.toString()}
									</HeadingLBold>
									<div className={classes.summaryLine}></div>
								</div>
								<div>
									<ConditionalRender
										show={isRepeatEnabled}
										onFalse={() => (
											<>
												<Caption disableMargin color="disabled-light">
													{translate('prepaid.loyalty.add.new.services.payment.date')}
												</Caption>
												<HeadingSBold color="disabled-light" disableMargin>
													{period.startDate.format(dateFormat)}
												</HeadingSBold>
											</>
										)}
										onTrue={() => (
											<>
												<div className={classes.summaryRepeatEnabledContainer}>
													<div>
														<Caption disableMargin color="disabled-light">
															{translate('prepaid.loyalty.add.new.services.start.date')}
														</Caption>
														<HeadingSBold disableMargin color="disabled-light">
															{period.startDate.format(dateFormat)}
														</HeadingSBold>
													</div>
													<ChevronRight
														color="disabled"
														fontSize="large"
														className={classes.summaryPaymentInfoIcon}
													/>
													<div>
														<Caption disableMargin color="disabled-light">
															{translate('prepaid.loyalty.add.new.services.end.date')}
														</Caption>
														<HeadingSBold disableMargin color="disabled-light">
															{period.endDate?.format(dateFormat)}
														</HeadingSBold>
													</div>
												</div>
												<div>
													<Caption disableMargin color="disabled-light">
														{translate('prepaid.loyalty.add.new.services.recurrence')}
													</Caption>
													<HeadingSBold disableMargin color="disabled-light">
														{getRecurrenceSummaryTab(periodicity, selectedPeriod)}
													</HeadingSBold>
												</div>
											</>
										)}
									/>

									<div className={classes.summaryLine}></div>
								</div>
								<div className={classes.summaryPaymentInfoContainer}>
									<ConditionalRender
										show={paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD}
										onTrue={() => (
											<CreditCard
												color="disabled"
												fontSize="large"
												className={classes.summaryPaymentInfoIcon}
											/>
										)}
										onFalse={() => (
											<Phone
												color="disabled"
												fontSize="large"
												className={classes.summaryPaymentInfoIcon}
											/>
										)}
									/>
									<div>
										<ConditionalRender
											show={paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD}
											onTrue={() => (
												<>
													<Caption disableMargin color="disabled-light">
														{translate('prepaid.loyalty.add.new.services.credit.card')}
													</Caption>
													<HeadingSBold disableMargin color="disabled-light">
														{translate(
															'prepaid.loyalty.add.new.services.credit.card.mask',
															creditCardNumber ? creditCardNumber.substring(12) : ''
														)}
													</HeadingSBold>
												</>
											)}
											onFalse={() => (
												<>
													<Caption disableMargin color="disabled-light">
														{translate(
															'prepaid.loyalty.add.new.services.master.postpaid.number'
														)}
													</Caption>
													<HeadingSBold disableMargin color="disabled-light">
														{masterNumber}
													</HeadingSBold>
												</>
											)}
										/>
									</div>
								</div>
								<div>
									<ConditionalRender
										show={!!selectedDonation}
										onTrue={() => (
											<>
												<div className={classes.summaryLine}></div>
												<Caption disableMargin color="disabled-light">
													{translate(
														'prepaid.loyalty.add.new.services.payment.donationAmount'
													)}
												</Caption>
												<HeadingSBold color="disabled-light" disableMargin>
													{translate('price', selectedDonation?.value || '')}
												</HeadingSBold>
											</>
										)}
									/>
								</div>
								<div>
									<div className={classes.summaryLine} />
									<Caption disableMargin color="disabled-light">
										{translate('payments.topup.totalAmount')}
									</Caption>
									<div className={classes.totalAmountWrapper}>
										<HeadingLBold disableMargin color="brand">
											{translate('price', totalAmount.toFixed(3))}
										</HeadingLBold>
									</div>
									<div className={classes.vatWrapper}>
										<BodyS disableMargin color="disabled-light">
											{translate('prepaid.loyalty.add.new.services.vat.included')}
										</BodyS>
									</div>
								</div>
							</div>
						</>
					)}
				/>
			</div>
		</div>
	);
};
export default SummaryTab;
